import React, { useEffect } from 'react'
import type { formFields, IFormBuilder } from '../../interfaces/IDispatchFormBuilder'
import { Container, Divider, Grid, Typography } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import dayjs from 'dayjs';
import BodyMarkings from '../DispatchRegistrationPeople/components/FormBuilderOccurrencePeople/ImageActionFormBuilder/BodyMarkings';
import { normilizePositionBodyMarkings } from '~/utils/format';
import { useHookstate } from '@hookstate/core';
import { getInjuryTypes } from '../../services';
import type { IInjuryType, IclickPositions } from '../DispatchRegistrationPeople/components/FormBuilderOccurrencePeople/ImageActionFormBuilder';

interface FieldsPrintFormProps {
  readonly formWithValuesResponse: IFormBuilder;
}



const renderBodyMarkingsField = (
  field: formFields,
  normalizedValueFront: readonly IclickPositions[],
  normalizedValueBack: readonly IclickPositions[],
  translate: (value: string) => string
) => {
  const injuries = [...normalizedValueFront, ...normalizedValueBack]
  const itensRender: number[] = []

  if (injuries.length === 0) {
    return (
      <Grid key={field.id} item xs={12}>
        <Typography variant="subtitle1">
          <b>
            {translate('No information')}
          </b>
        </Typography>
      </Grid>
    )
  } else {
    return (
      <Grid key={field.id} item xs={12}>
        <Typography variant="subtitle1">
          <b>
            {field.name}
          </b>
        </Typography>
        <Grid item xs={12} width={'100%'} display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2} sx={{ flexWrap: 'wrap' }}>
          {
            Array.isArray(injuries) && injuries.map((pos) => {
              if (itensRender.some((item) => item === pos.injury_type_id)) return null;

              // eslint-disable-next-line functional/immutable-data
              itensRender.push(pos.injury_type_id)
              return (
                <Typography variant='subtitle2' key={`print-${pos.xPosition}-${pos.yPosition}-${pos.injury_type_id}`}>
                  {`${pos.acronym} : ${pos.name}`}
                </Typography>
              )
            })
          }
        </Grid>
        <Grid item display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} marginTop={2}>
          <BodyMarkings
            clickPositions={[...normalizedValueFront]}
            side='front'
            isPrint={true}
          />
          <BodyMarkings
            clickPositions={[...normalizedValueBack]}
            side='back'
            isPrint={true}
          />
        </Grid>
      </Grid>
    )
  }
};

const renderRegularField = (field: formFields, formatedValues: (field: formFields) => string | null) => {
  return (
    field.value && (
      <Grid key={field.id} item xs={4} sx={{ marginTop: 2 }}>
        <Typography variant="subtitle2">
          <b>
            {`${field.name}: `}
          </b>
          {formatedValues(field)}
        </Typography>
      </Grid>
    )
  )
};

export default function FieldsPrintForm({ formWithValuesResponse }: FieldsPrintFormProps) {
  const { translate } = useLang();

  const formGroups = formWithValuesResponse?.form_groups;

  const normalizedValueFront = useHookstate<readonly IclickPositions[]>([])
  const normalizedValueBack = useHookstate<readonly IclickPositions[]>([])

  const processBodyMarkings = (injuryTypesData: IInjuryType) => {
    formGroups.forEach((formGroup) => {
      formGroup.form_fields
        .filter((formField) => formField.type === "body-markings")
        .forEach((field) => {
          const frontFormated = JSON.parse(field.value)['front'];
          const backFormated = JSON.parse(field.value)['back'];

          if (frontFormated && frontFormated !== 'undefined') {
            const front = normilizePositionBodyMarkings({
              value: frontFormated,
              injuryTypes: injuryTypesData
            });
            normalizedValueFront.set(front);
          }

          if (backFormated && backFormated !== 'undefined') {
            const back = normilizePositionBodyMarkings({
              value: backFormated,
              injuryTypes: injuryTypesData
            });
            normalizedValueBack.set(back);
          }

        });
    });
  }

  useEffect(() => {
    const fetchInjuryTypes = async () => {
      try {
        const InjuryTypesResponse: IInjuryType = await getInjuryTypes()

        processBodyMarkings(InjuryTypesResponse);

        return true;
      } catch (error) {
        console.error('Erro ao buscar injury types:', error);
      }
    };

    fetchInjuryTypes();
  }, []);

  const formatedValues = (field: formFields) => {
    switch (field.type) {
      case "boolean":
        return field.value === 'true' ? translate("Yes") : translate("No");
      case "date":
        return field.value ? dayjs(field.value).format("DD/MM/YYYY") : translate("Not informed");
      case "date_time":
        return field.value ? dayjs(field.value).format("DD/MM/YYYY HH:mm:ss") : translate("Not informed");
      default:
        return field.value;
    }
  };

  const getBooleanTrueFields = (fields: readonly formFields[]) => {
    return fields
      .filter((field) => field.type === "boolean" && field.value === 'true')
      .map((field) => translate(field.name as string));
  };

  return (
    <Container fixed={false}>
      <Grid container xs={12} sx={{ marginBottom: 2 }}>
        <Grid container xs={12} >
          {formGroups.map((formGroup) => {

            const hasAnyValue = formGroup.form_fields.some((field) => {
              if (!field.value || field.value === null || field.value === undefined) {
                return false;
              }

              if (field.type === "boolean") {
                return field.value === 'true';
              }

              return true;
            });
            const hasBodyMarkings = formGroup.form_fields.some((field) => field.type === "body-markings" && field.value);

            return (
              <React.Fragment key={formGroup.id} >
                <Grid
                  container
                  xs={12}
                  sx={{
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid'
                  }}>
                  <Grid
                    item
                    xs={12}
                  >
                    <Divider sx={{ marginY: 2 }}>
                      <Typography variant="subtitle1">
                        <b>
                          {translate(formGroup.name as string)}
                        </b>
                      </Typography>
                    </Divider>
                  </Grid>

                  {!hasAnyValue && (
                    <Typography key={formGroup.id} variant="subtitle2" align="center" sx={{ marginTop: 2, marginBottom: 2 }}>
                      <b>
                        {translate('No information')}
                      </b>
                    </Typography>
                  )}
                  {hasAnyValue && (
                    <>

                      {(() => {
                        const booleanTrueFields = getBooleanTrueFields(formGroup.form_fields);

                        if (booleanTrueFields.length > 0) {
                          return (
                            <Grid item xs={12} sx={{ marginTop: 2 }}>
                              <Typography variant="subtitle2">
                                <b>
                                  {`${translate('Identificado')}: `}
                                </b>
                                {booleanTrueFields.join(', ')}
                              </Typography>
                            </Grid>
                          );
                        }

                        return null;
                      })()}
                      {hasBodyMarkings && (
                        <div style={{ pageBreakBefore: 'always', breakBefore: 'page' }} />
                      )}
                      {formGroup.form_fields.map((field) => {
                        if (field.type === "body-markings" && field.value) {
                          return renderBodyMarkingsField(
                            field,
                            normalizedValueFront?.get(),
                            normalizedValueBack?.get(),
                            translate
                          );
                        }

                        if (field.type === "boolean") {
                          return null;
                        }

                        return renderRegularField(field, formatedValues);
                      })}
                    </>
                  )}
                </Grid>
              </React.Fragment>
            )
          })}
        </Grid>
      </Grid>
    </Container>
  )
}